import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Music, Play, Zap, Search, Heart, Users, Baby, TrendingUp, Smile, 
    Volume2, Rocket, Drumstick, Sunrise, Filter, SlidersHorizontal, ChevronDown, Clock
} from "lucide-react"; 

// --- Framer Motion Variants (Unchanged) ---
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08, 
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 250, 
            damping: 25,
        },
    },
};

// --- Data Definitions (Unchanged) ---
const AGE_GROUPS = [
    { name: "All Ages", key: "All", icon: Users }, 
    { name: "2-3 Years", key: "2-3", icon: Baby },
    { name: "4-5 Years", key: "4-5", icon: Smile },
    { name: "6+ Years", key: "6+", icon: TrendingUp },
];

const RHYTHM_CATEGORIES = [
    { name: "All Rhythm Games", icon: Music, filterKey: "All" },
    { name: "Rhythm & Music", icon: Volume2, filterKey: "Rhythm & Music" },
    { name: "High Energy", icon: Zap, filterKey: "High Energy" },
    { name: "Emotional Expression", icon: Heart, filterKey: "Creativity & Emotion" },
];

const MUSIC_DANCE_GAMES = [
    { id: 2, title: "Echo Quest 🎵", desc: "Repeat rhythms and melodies to navigate sound puzzles. Great for timing and pitch recognition.", btn: "Begin Echo Quest", category: "Rhythm & Music", icon: Music, ageRange: "4-5", color: "from-purple-400 to-indigo-500", tempo: "Moderate", movement: "Clapping & Singing" },
    { id: 3, title: "Vibe Racer 🚀", desc: "Control your speed with high-energy words and claps. A dynamic game for loud, clear articulation.", btn: "Start Racing", category: "High Energy", icon: Rocket, ageRange: "6+", color: "from-red-400 to-pink-500", tempo: "Fast", movement: "Running & Jumping" },
    { id: 6, title: "Mood Melody Mixer 😊", desc: "Express emotions vocally (tone) to change the scene. Builds emotional vocabulary.", btn: "Mix the Mood", category: "Creativity & Emotion", icon: Heart, ageRange: "4-5", color: "from-rose-400 to-fuchsia-500", tempo: "Slow", movement: "Facial Expressions" },
    { id: 11, title: "Beat Boxer Blitz 🥁", desc: "Copy complex verbal drum patterns. Develops vocal control and syncopation.", btn: "Start the Beat", category: "Rhythm & Music", icon: Drumstick, ageRange: "6+", color: "from-yellow-500 to-orange-400", tempo: "Varies (Fast)", movement: "Tapping & Beatboxing" },
    { id: 12, title: "Sunrise Stretch Song 🌅", desc: "Follow gentle vocal instructions to stretch and move slowly. Focuses on body awareness and soft speech.", btn: "Stretch Up", category: "High Energy", icon: Sunrise, ageRange: "2-3", color: "from-sky-400 to-blue-300", tempo: "Slow", movement: "Gentle Stretching" },
    { id: 13, title: "Tone Painter 🎨", desc: "Use high and low pitches to color in scenes. Focuses on vocal range and control.", btn: "Start Painting", category: "Rhythm & Music", icon: Volume2, ageRange: "4-5", color: "from-teal-400 to-cyan-500", tempo: "Moderate", movement: "Arm Movements" },
];

// --- Music Game Card Component (Unchanged) ---
const MusicGameCard = ({ game }) => {
    const PlaceholderIcon = game.icon; 
    const ageGroupName = AGE_GROUPS.find(g => g.key === game.ageRange)?.name || "All Ages";

    return (
      <motion.article
        variants={itemVariants}
        layout 
        className="flex flex-col bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer border border-transparent hover:border-4 hover:border-purple-400/50" 
      >
        <div className={`h-36 flex items-center justify-center p-6 bg-gradient-to-br ${game.color} relative`}>
          <PlaceholderIcon className="w-14 h-14 text-white opacity-90 drop-shadow-xl z-10" aria-hidden="true" />
        </div>
        <div className="flex flex-col p-5 md:p-6 flex-grow">
          <h2 className="text-xl md:text-2xl font-extrabold mb-2 text-gray-800 leading-snug">{game.title}</h2>
          <p className="text-gray-600 text-sm flex-grow mb-4 line-clamp-3">{game.desc}</p>
          <div className="flex flex-wrap gap-2 mb-5">
              <span className="text-xs font-semibold text-pink-600 bg-pink-100 px-3 py-1 rounded-full w-fit">Move: {game.movement}</span>
              <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-3 py-1 rounded-full w-fit">Tempo: {game.tempo}</span>
              <span className="text-xs font-bold text-gray-700 bg-gray-200 px-3 py-1 rounded-full w-fit">{ageGroupName}</span>
          </div>
          <motion.button
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-white shadow-lg shadow-purple-200 transition-colors text-base"
            whileHover={{ scale: 1.02, boxShadow: "0 10px 15px -3px rgba(168, 85, 247, 0.5), 0 4px 6px -2px rgba(168, 85, 247, 0.05)" }} 
            whileTap={{ scale: 0.98 }}
            onClick={() => console.log(`Starting game: ${game.id}`)}
          >
            <Play className="w-5 h-5" aria-hidden="true" />
            {game.btn}
          </motion.button>
        </div>
      </motion.article>
    );
};


// --- Filter Bar Component (Unchanged) ---
const FilterBar = ({ activeAge, setActiveAge, activeCategory, setActiveCategory, isFilterOpen, setIsFilterOpen }) => {
    const activeCategoryName = RHYTHM_CATEGORIES.find(c => c.name === activeCategory)?.name;

    return (
        <div className="w-full mb-8">
            {/* Filter Toggle Button (Always visible on mobile, combined with search on desktop) */}
            <motion.div 
                className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white rounded-xl shadow-xl border border-purple-100 lg:p-3 lg:gap-5"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
            >
                <div className="flex items-center gap-3 text-lg font-bold text-purple-600">
                    <SlidersHorizontal className="w-6 h-6"/> Filters
                </div>

                <div className="hidden lg:flex flex-wrap items-center gap-3">
                    {/* Desktop Age Filters */}
                    {AGE_GROUPS.map((age) => {
                        const isActive = activeAge === age.key;
                        return (
                            <motion.button
                                key={age.key}
                                onClick={() => setActiveAge(age.key)}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${
                                    isActive
                                        ? `bg-pink-500 text-white shadow-md`
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Users className="w-4 h-4" />
                                {age.name}
                            </motion.button>
                        );
                    })}
                </div>

                {/* Mobile/Default Toggle for Category */}
                <motion.button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="flex items-center gap-2 px-4 py-2 font-bold rounded-xl text-sm transition-all bg-purple-100 text-purple-600 hover:bg-purple-200"
                    whileTap={{ scale: 0.98 }}
                    aria-expanded={isFilterOpen}
                >
                    <Filter className="w-5 h-5" />
                    {activeCategoryName} ({isFilterOpen ? 'Hide' : 'Change'})
                    <ChevronDown className={`w-4 h-4 transition-transform ${isFilterOpen ? 'rotate-180' : 'rotate-0'}`} />
                </motion.button>
            </motion.div>

            {/* Collapsible Filter Panel (Mobile/Tablet View) */}
            <AnimatePresence>
                {isFilterOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden bg-white mt-4 p-6 rounded-xl shadow-2xl border border-pink-100"
                    >
                        {/* Age Filter Section (Repeated for Mobile/Collapsible View) */}
                        <h3 className="text-xl font-extrabold text-purple-600 mb-4 pb-2 border-b border-purple-100 flex items-center gap-2">
                            <Users className="w-5 h-5"/> Age Groups
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-8">
                            {AGE_GROUPS.map((age) => {
                                const isActive = activeAge === age.key;
                                const AgeIcon = age.icon;
                                return (
                                    <motion.button
                                        key={`mobile-${age.key}`}
                                        onClick={() => setActiveAge(age.key)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                                            isActive
                                                ? `bg-purple-600 text-white shadow-md ring-2 ring-purple-300`
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <AgeIcon className="w-4 h-4" />
                                        {age.name}
                                    </motion.button>
                                );
                            })}
                        </div>
                        
                        {/* Category Filter Section */}
                        <h3 className="text-xl font-extrabold text-purple-600 mb-4 pb-2 border-b border-purple-100 flex items-center gap-2">
                            <Volume2 className="w-5 h-5"/> Focus & Theme
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {RHYTHM_CATEGORIES.map((category) => {
                                const isActive = activeCategory === category.name;
                                const CategoryIcon = category.icon;
                
                                return (
                                    <motion.button
                                        key={category.name}
                                        onClick={() => setActiveCategory(category.name)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl font-semibold text-base transition-all text-left ${
                                            isActive
                                                ? "bg-pink-100 text-pink-700 ring-2 ring-pink-300 shadow-sm"
                                                : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                                        }`}
                                        whileHover={{ x: isActive ? 0 : 5 }}
                                    >
                                        <CategoryIcon className="w-5 h-5 min-w-5 text-pink-500" aria-hidden="true" />
                                        {category.name}
                                    </motion.button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// --- NEW Coming Soon Overlay Component ---
const ComingSoonOverlay = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center pointer-events-none"
    >
        <motion.div
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100, damping: 10, delay: 0.3 }}
            className="p-10 bg-purple-500/95 rounded-3xl shadow-2xl text-center border-4 border-white transform rotate-3"
        >
            <Clock className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-5xl font-extrabold text-white tracking-wider">
                COMING SOON!
            </h3>
            <p className="text-xl text-purple-100 mt-2">
                This page is currently under development.
            </p>
        </motion.div>
    </motion.div>
);

// --- Main Page Component ---
export default function TalkyToonRhythmPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("All Rhythm Games");
    const [activeAge, setActiveAge] = useState("All"); 
    const [isFilterOpen, setIsFilterOpen] = useState(false); 

    //  New State for Coming Soon 
    const [isComingSoon, setIsComingSoon] = useState(true); 

    // --- Filtering Logic (Unchanged) ---
    const filteredGames = useMemo(() => {
        return MUSIC_DANCE_GAMES.filter((game) => {
            const categoryKey = RHYTHM_CATEGORIES.find(c => c.name === activeCategory)?.filterKey;
            const categoryMatch = categoryKey === "All" || game.category === categoryKey;

            const searchMatch = game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                game.desc.toLowerCase().includes(searchTerm.toLowerCase()) || 
                game.tempo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                game.movement.toLowerCase().includes(searchTerm.toLowerCase()); 

            const ageMatch = activeAge === "All" || game.ageRange === activeAge;

            return categoryMatch && searchMatch && ageMatch;
        });
    }, [searchTerm, activeCategory, activeAge]);


    return (
        // 1. Full-Screen Container with BG Gradient (min-h-screen for full height)
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-100 text-gray-800 font-sans relative">
            
            <main className="relative z-10 pt-10 pb-20 w-full"> 
                
                {/* 🎶 Page Heading & Intro */}
                <header className="w-full pb-10 px-4 sm:px-6 lg:px-8">
                    <motion.h1
                        className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-center tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-pink-600 drop-shadow-lg"
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        Rhythm & Movement Studio
                    </motion.h1>
                    <motion.p
                        className="text-center text-lg md:text-xl text-gray-700 leading-relaxed mt-4 max-w-3xl mx-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        Tap, sing, and dance your way to learning! Find the perfect beat for any mood.
                    </motion.p>
                </header>

                {/* --- MAIN FULL-WIDTH CONTENT AREA --- */}
                <section className="w-full px-4 sm:px-6 lg:px-8 relative">
                    
                    {/*  COMING SOON OVERLAY  */}
                    {isComingSoon && <ComingSoonOverlay />}
                    
                    {/* Content below will be blurred/covered by the overlay */}

                    {/* 1. FILTER BAR (Top Horizontal Placement) */}
                    <FilterBar
                        activeAge={activeAge} 
                        setActiveAge={setActiveAge} 
                        activeCategory={activeCategory} 
                        setActiveCategory={setActiveCategory}
                        isFilterOpen={isFilterOpen}
                        setIsFilterOpen={setIsFilterOpen}
                    />

                    {/* 2. SEARCH BAR */}
                    <motion.div 
                        className="relative mb-12 shadow-2xl rounded-2xl bg-white border-4 border-pink-100/70"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                    >
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-pink-500/90" aria-hidden="true" />
                        <input
                            type="text"
                            placeholder="Search by tempo, movement, or title..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-6 py-5 text-lg rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-200 transition-shadow bg-transparent"
                        />
                    </motion.div>
                    
                    {/* 3. RESULTS HEADER & GRID */}
                    <h2 className="text-3xl font-extrabold text-gray-800 mb-8 flex items-center gap-3">
                        <SlidersHorizontal className="w-7 h-7 text-purple-600"/>
                        {activeCategory} Results (<span className="text-pink-500">{filteredGames.length}</span>)
                    </h2>

                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" 
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        layout 
                    >
                        <AnimatePresence mode="popLayout">
                        {filteredGames.length > 0 ? (
                            filteredGames.map((game) => (
                                <MusicGameCard key={game.id} game={game} />
                            ))
                        ) : (
                            <motion.div 
                                key="no-results"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="sm:col-span-2 lg:col-span-3 xl:col-span-4 text-center py-20 px-8 bg-white/70 backdrop-blur-sm rounded-3xl shadow-inner border-2 border-dashed border-purple-300"
                            >
                                <Music className="w-16 h-16 text-purple-400 mx-auto mb-6" aria-hidden="true" />
                                <p className="text-2xl text-gray-700 font-extrabold">Silence... No rhythm adventures match your current selection.</p>
                                <p className="text-lg text-gray-500 mt-2">Try adjusting your filters or simplifying your search term to find the beat!</p>
                            </motion.div>
                        )}
                        </AnimatePresence>
                    </motion.div>
                </section>
            </main>
        </div>
    );
}